import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchCampaigns, createCampaign, updateCampaign, deleteCampaign, sendCampaign, testCampaign } from './campaignsSlice';
import { fetchCustomerGroups, fetchAllTags } from '../customerGroups/customerGroupsSlice';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';

const CampaignCard = ({ campaign, onEdit, onDelete, onSend, onTest }) => (
  <Card padding="lg" hover>
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'flex-start',
      marginBottom: 'var(--space-4)'
    }}>
      <div style={{ flex: 1 }}>
        <h3 style={{ 
          fontSize: 'var(--font-size-lg)', 
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-gray-900)',
          margin: '0 0 var(--space-2) 0'
        }}>
          {campaign.name}
        </h3>
        <p style={{ 
          fontSize: 'var(--font-size-sm)', 
          color: 'var(--color-gray-600)',
          margin: '0 0 var(--space-3) 0'
        }}>
          {campaign.description}
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
          <Badge variant={campaign.status === 'active' ? 'success' : campaign.status === 'paused' ? 'warning' : 'default'}>
            {campaign.status}
          </Badge>
          <Badge variant="outline">
            {campaign.type}
          </Badge>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        {campaign.status === 'active' && (
          <>
            <Button variant="primary" size="sm" onClick={() => onSend(campaign._id || campaign.id)}>
              Send
            </Button>
            <Button variant="outline" size="sm" onClick={() => onTest(campaign._id || campaign.id)}>
              Test
            </Button>
          </>
        )}
        <Button variant="ghost" size="sm" onClick={() => onEdit(campaign)}>
          Edit
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(campaign._id || campaign.id)}>
          Delete
        </Button>
      </div>
    </div>
    
    <div style={{ 
      display: 'grid', 
      gridTemplateColumns: 'repeat(3, 1fr)', 
      gap: 'var(--space-4)',
      padding: 'var(--space-4)',
      background: 'var(--color-gray-50)',
      borderRadius: 'var(--radius-md)'
    }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ 
          fontSize: 'var(--font-size-2xl)', 
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--color-gray-900)',
          margin: '0 0 var(--space-1) 0'
        }}>
          {campaign.recipients || 0}
        </p>
        <p style={{ 
          fontSize: 'var(--font-size-sm)', 
          color: 'var(--color-gray-600)',
          margin: 0
        }}>
          Recipients
        </p>
      </div>
      <div style={{ textAlign: 'center' }}>
        <p style={{ 
          fontSize: 'var(--font-size-2xl)', 
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--color-gray-900)',
          margin: '0 0 var(--space-1) 0'
        }}>
          {campaign.opens || 0}
        </p>
        <p style={{ 
          fontSize: 'var(--font-size-sm)', 
          color: 'var(--color-gray-600)',
          margin: 0
        }}>
          Opens
        </p>
      </div>
      <div style={{ textAlign: 'center' }}>
        <p style={{ 
          fontSize: 'var(--font-size-2xl)', 
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--color-gray-900)',
          margin: '0 0 var(--space-1) 0'
        }}>
          {campaign.clicks || 0}
        </p>
        <p style={{ 
          fontSize: 'var(--font-size-sm)', 
          color: 'var(--color-gray-600)',
          margin: 0
        }}>
          Clicks
        </p>
      </div>
    </div>
  </Card>
);

export default function Campaigns() {
  const dispatch = useDispatch();
  const { campaigns, loading, error } = useSelector(state => state.campaigns);
  const { groups, allTags } = useSelector(state => state.customerGroups);
  const { token } = useSelector(state => state.auth);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    subject: '',
    body: '',
    type: 'Email',
    status: 'draft',
    audienceTag: '',
    targeting: {
      type: 'all',
      groups: [],
      tags: [],
      customCriteria: {
        conditions: []
      }
    }
  });

  // Fetch campaigns from backend
  useEffect(() => {
    if (token) {
      dispatch(fetchCampaigns());
      dispatch(fetchCustomerGroups());
      dispatch(fetchAllTags());
    }
  }, [dispatch, token]);

  const handleCreateCampaign = async () => {
    try {
      const campaignData = {
        ...formData,
        channel: formData.type.toLowerCase()
      };
      await dispatch(createCampaign({ campaignData })).unwrap();
      setShowCreateModal(false);
      setFormData({ 
        name: '', 
        description: '', 
        subject: '', 
        body: '', 
        type: 'Email', 
        status: 'draft', 
        audienceTag: '',
        fromEmail: '',
        targeting: {
          type: 'all',
          groups: [],
          tags: [],
          customCriteria: {
            conditions: []
          }
        }
      });
    } catch (err) {
      console.error('Error creating campaign:', err);
    }
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      description: campaign.description || '',
      subject: campaign.subject || '',
      body: campaign.body || '',
      type: campaign.type,
      status: campaign.status,
      audienceTag: campaign.audienceTag || '',
      fromEmail: campaign.fromEmail || '',
      targeting: campaign.targeting || {
        type: 'all',
        groups: [],
        tags: [],
        customCriteria: {
          conditions: []
        }
      }
    });
    setShowCreateModal(true);
  };

  const handleUpdateCampaign = async () => {
    try {
      const campaignData = {
        ...formData,
        channel: formData.type.toLowerCase()
      };
      await dispatch(updateCampaign({ 
        campaignId: editingCampaign._id, 
        campaignData, 
        token 
      })).unwrap();
      setShowCreateModal(false);
      setEditingCampaign(null);
      setFormData({ 
        name: '', 
        description: '', 
        subject: '', 
        body: '', 
        type: 'Email', 
        status: 'draft', 
        audienceTag: '',
        fromEmail: '',
        targeting: {
          type: 'all',
          groups: [],
          tags: [],
          customCriteria: {
            conditions: []
          }
        }
      });
    } catch (err) {
      console.error('Error updating campaign:', err);
    }
  };

  const handleDeleteCampaign = async (id) => {
    try {
      await dispatch(deleteCampaign({ campaignId: id })).unwrap();
    } catch (err) {
      console.error('Error deleting campaign:', err);
    }
  };

  const handleSendCampaign = async (campaignId) => {
    try {
      const result = await dispatch(sendCampaign({ campaignId })).unwrap();
      alert(`Campaign sent successfully! Sent to ${result.sentCount} recipients.`);
      dispatch(fetchCampaigns()); // Refresh campaigns
    } catch (err) {
      alert('Error sending campaign: ' + err.message);
      console.error('Error sending campaign:', err);
    }
  };

  const handleTestCampaign = async (campaignId) => {
    const testEmail = prompt('Enter test email address:');
    if (!testEmail) return;

    try {
      const result = await dispatch(testCampaign({ campaignId, testEmail })).unwrap();
      alert(`Test campaign sent successfully to ${testEmail}!`);
    } catch (err) {
      alert('Error sending test campaign: ' + err.message);
      console.error('Error sending test campaign:', err);
    }
  };

  return (
    <Shell>
      <FeatureGate featureId="marketing.email" upgradeMessage="Upgrade to access email marketing campaigns and automation features">
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: 'var(--space-8)'
        }}>
          <div>
            <h1 style={{ 
              fontSize: 'var(--font-size-4xl)', 
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--color-gray-900)',
              margin: '0 0 var(--space-2) 0'
            }}>
              Marketing Campaigns
            </h1>
            <p style={{ 
              fontSize: 'var(--font-size-lg)', 
              color: 'var(--color-gray-600)',
              margin: 0
            }}>
              Create and manage your marketing campaigns
            </p>
          </div>
          <Button 
            variant="primary"
            onClick={() => setShowCreateModal(true)}
          >
            Create Campaign
          </Button>
        </div>

        {/* Stats Overview */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: 'var(--space-6)',
          marginBottom: 'var(--space-8)'
        }}>
          <Card padding="lg">
            <div style={{ textAlign: 'center' }}>
              <p style={{ 
                fontSize: 'var(--font-size-3xl)', 
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--color-gray-900)',
                margin: '0 0 var(--space-1) 0'
              }}>
                {campaigns.length}
              </p>
              <p style={{ 
                fontSize: 'var(--font-size-sm)', 
                color: 'var(--color-gray-600)',
                margin: 0
              }}>
                Total Campaigns
              </p>
            </div>
          </Card>
          <Card padding="lg">
            <div style={{ textAlign: 'center' }}>
              <p style={{ 
                fontSize: 'var(--font-size-3xl)', 
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--color-gray-900)',
                margin: '0 0 var(--space-1) 0'
              }}>
                {campaigns.filter(c => c.status === 'active').length}
              </p>
              <p style={{ 
                fontSize: 'var(--font-size-sm)', 
                color: 'var(--color-gray-600)',
                margin: 0
              }}>
                Active Campaigns
              </p>
            </div>
          </Card>
          <Card padding="lg">
            <div style={{ textAlign: 'center' }}>
              <p style={{ 
                fontSize: 'var(--font-size-3xl)', 
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--color-gray-900)',
                margin: '0 0 var(--space-1) 0'
              }}>
                {campaigns.reduce((sum, c) => sum + c.recipients, 0).toLocaleString()}
              </p>
              <p style={{ 
                fontSize: 'var(--font-size-sm)', 
                color: 'var(--color-gray-600)',
                margin: 0
              }}>
                Total Recipients
              </p>
            </div>
          </Card>
        </div>

        {/* Campaigns List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
            <p>Loading campaigns...</p>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
            <p style={{ color: 'var(--color-red-600)' }}>Error: {error}</p>
          </div>
        ) : campaigns.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
            <p>No campaigns found. Create your first campaign to get started.</p>
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
            gap: 'var(--space-6)'
          }}>
            {campaigns.map(campaign => (
              <CampaignCard
                key={campaign._id || campaign.id}
                campaign={campaign}
                onEdit={handleEditCampaign}
                onDelete={handleDeleteCampaign}
                onSend={handleSendCampaign}
                onTest={handleTestCampaign}
              />
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        <Modal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setEditingCampaign(null);
            setFormData({ 
              name: '', 
              description: '', 
              subject: '', 
              body: '', 
              type: 'Email', 
              status: 'draft', 
              audienceTag: '',
              targeting: {
                type: 'all',
                groups: [],
                tags: [],
                customCriteria: {
                  conditions: []
                }
              }
            });
          }}
          title={editingCampaign ? 'Edit Campaign' : 'Create Campaign'}
          size="md"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <Input
              label="Campaign Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter campaign name"
            />
            <Input
              label="Subject Line"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="Enter email subject line"
            />
            {formData.type === 'Email' && (
              <Input
                label="From Email Address"
                value={formData.fromEmail || ''}
                onChange={(e) => setFormData({ ...formData, fromEmail: e.target.value })}
                placeholder="e.g., marketing@yourstore.com"
                type="email"
              />
            )}
            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--color-gray-700)',
                marginBottom: 'var(--space-2)'
              }}>
                Campaign Content
              </label>
              <textarea
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                placeholder="Enter your campaign message content here..."
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-base)',
                  fontFamily: 'var(--font-family)',
                  resize: 'vertical',
                  minHeight: '150px'
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--color-gray-700)',
                marginBottom: 'var(--space-2)'
              }}>
                Description (Internal)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Internal description for your reference"
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-base)',
                  fontFamily: 'var(--font-family)',
                  resize: 'vertical',
                  minHeight: '80px'
                }}
              />
            </div>
            <Input
              label="Audience Tag (Optional)"
              value={formData.audienceTag}
              onChange={(e) => setFormData({ ...formData, audienceTag: e.target.value })}
              placeholder="e.g., vip-customers, newsletter-subscribers"
            />
            
            {/* Targeting Section */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: 'var(--font-size-sm)', 
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--color-gray-700)',
                marginBottom: 'var(--space-2)'
              }}>
                Target Audience
              </label>
              <select
                value={formData.targeting.type}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  targeting: { ...formData.targeting, type: e.target.value }
                })}
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: 'var(--border-radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  marginBottom: 'var(--space-3)'
                }}
              >
                <option value="all">All Customers</option>
                <option value="groups">Specific Groups</option>
                <option value="tags">Specific Tags</option>
                <option value="custom">Custom Criteria</option>
              </select>
              
              {/* Groups Selection */}
              {formData.targeting.type === 'groups' && (
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label style={{ 
                    fontSize: 'var(--font-size-sm)', 
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--color-gray-700)',
                    marginBottom: 'var(--space-2)',
                    display: 'block'
                  }}>
                    Select Groups
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                    {groups.map(group => (
                      <label key={group._id} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 'var(--space-1)',
                        padding: 'var(--space-2)',
                        border: '1px solid var(--color-gray-300)',
                        borderRadius: 'var(--border-radius-md)',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={formData.targeting.groups.includes(group._id)}
                          onChange={(e) => {
                            const newGroups = e.target.checked
                              ? [...formData.targeting.groups, group._id]
                              : formData.targeting.groups.filter(id => id !== group._id);
                            setFormData({
                              ...formData,
                              targeting: { ...formData.targeting, groups: newGroups }
                            });
                          }}
                        />
                        <span style={{ fontSize: 'var(--font-size-sm)' }}>{group.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Tags Selection */}
              {formData.targeting.type === 'tags' && (
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label style={{ 
                    fontSize: 'var(--font-size-sm)', 
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--color-gray-700)',
                    marginBottom: 'var(--space-2)',
                    display: 'block'
                  }}>
                    Select Tags
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                    {allTags.map(tag => (
                      <label key={tag} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 'var(--space-1)',
                        padding: 'var(--space-2)',
                        border: '1px solid var(--color-gray-300)',
                        borderRadius: 'var(--border-radius-md)',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={formData.targeting.tags.includes(tag)}
                          onChange={(e) => {
                            const newTags = e.target.checked
                              ? [...formData.targeting.tags, tag]
                              : formData.targeting.tags.filter(t => t !== tag);
                            setFormData({
                              ...formData,
                              targeting: { ...formData.targeting, tags: newTags }
                            });
                          }}
                        />
                        <span style={{ fontSize: 'var(--font-size-sm)' }}>{tag}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--color-gray-700)',
                  marginBottom: 'var(--space-2)'
                }}>
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-base)',
                    fontFamily: 'var(--font-family)'
                  }}
                >
                  <option value="Email">Email</option>
                  <option value="SMS">SMS</option>
                  <option value="Push">In-App Notification</option>
                </select>
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--color-gray-700)',
                  marginBottom: 'var(--space-2)'
                }}>
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-base)',
                    fontFamily: 'var(--font-family)'
                  }}
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                </select>
              </div>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: 'var(--space-3)',
              marginTop: 'var(--space-6)'
            }}>
              <Button 
                variant="secondary"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingCampaign(null);
                  setFormData({ 
                    name: '', 
                    description: '', 
                    subject: '', 
                    body: '', 
                    type: 'Email', 
                    status: 'draft', 
                    audienceTag: '',
                    targeting: {
                      type: 'all',
                      groups: [],
                      tags: [],
                      customCriteria: {
                        conditions: []
                      }
                    }
                  });
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="primary"
                onClick={editingCampaign ? handleUpdateCampaign : handleCreateCampaign}
                disabled={!formData.name.trim()}
              >
                {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
              </Button>
            </div>
          </div>
        </Modal>
        </div>
      </FeatureGate>
    </Shell>
  );
}
